/**
 * LuvTune Quiz Funnel V4 — Updated Pricing
 * Two clear tiers, tap to select. Details panel updates per tier.
 * Song Only: $29.95 | Complete Gift: $54.95
 */

class LuvTuneQuizV4 {
  constructor(options = {}) {
    this.containerId = options.containerId || 'luvtune-quiz';
    this.container = document.getElementById(this.containerId);

    if (!this.container) return;

    this.totalSteps = 6;
    this.currentStep = 1;
    this.answers = {};
    this.storageKey = 'luvtune_quiz_v4_progress';
    this.selectedTier = 'ultimate';
    this.exitModalShown = false;

    this.steps = null;
    this.progressBar = null;
    this.progressPercent = null;

    this.valueMessages = {
      1: "Let's create something special...",
      2: 'Great choice! Your song is taking shape...',
      3: 'This is going to be beautiful...',
      4: 'Perfect voice matched! Almost there...',
      5: 'The more you share, the more personal it gets!',
      6: 'Your personalized song package is ready!'
    };

    this.init();
  }

  init() {
    this.cacheElements();
    this.bindEvents();
    this.loadSavedProgress();
    this.setupExitIntent();
    this.setupCharCounters();
    this.setupDateMin();
    this.setupGeoPricing();
    this.showStep(this.currentStep);

    if (window.location.hash === '#luvtune-quiz' || window.location.hash === '#luvtune-quiz-section') {
      setTimeout(() => {
        this.container.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    }
  }

  cacheElements() {
    this.steps = this.container.querySelectorAll('[data-quiz-step]');
    this.progressBar = this.container.querySelector('[data-progress-bar]');
    this.progressPercent = this.container.querySelector('[data-progress-percent]');
  }

  bindEvents() {
    this.container.addEventListener('click', (e) => {
      // Next / Prev
      if (e.target.matches('[data-quiz-next]') || e.target.closest('[data-quiz-next]')) {
        e.preventDefault();
        this.nextStep();
      }
      if (e.target.matches('[data-quiz-prev]') || e.target.closest('[data-quiz-prev]')) {
        e.preventDefault();
        this.prevStep();
      }

      // Option cards (steps 1-5)
      const optionCard = e.target.closest('[data-quiz-option]');
      if (optionCard) this.handleOptionSelect(optionCard);

      // Bundle tier selection (step 6)
      const tierBtn = e.target.closest('[data-select-tier]');
      if (tierBtn) {
        e.preventDefault();
        this.selectTier(tierBtn.dataset.selectTier);
      }

      // Submit CTA
      if (e.target.matches('.qb-cta[data-quiz-submit]') || e.target.closest('.qb-cta[data-quiz-submit]')) {
        e.preventDefault();
        this.handleSubmit();
      }
    });

    this.container.addEventListener('input', (e) => {
      if (e.target.matches('[data-quiz-field]')) this.handleFieldInput(e.target);
      if (e.target.matches('.quiz-textarea')) this.updateCharCount(e.target);
    });
  }

  // ========================
  // STEP MANAGEMENT
  // ========================

  showStep(stepNumber, shouldScroll = false) {
    this.steps.forEach(step => {
      step.classList.remove('quiz-step--active');
      step.setAttribute('aria-hidden', 'true');
    });

    const currentStepEl = this.container.querySelector(`[data-quiz-step="${stepNumber}"]`);
    if (currentStepEl) {
      currentStepEl.classList.add('quiz-step--active');
      currentStepEl.setAttribute('aria-hidden', 'false');

      if (shouldScroll) {
        this.container.scrollIntoView({ behavior: 'smooth', block: 'start' });
        const firstFocusable = currentStepEl.querySelector(
          'input:not([type="hidden"]), select, textarea, button[data-quiz-option], button[data-quiz-next]'
        );
        if (firstFocusable) {
          setTimeout(() => firstFocusable.focus({ preventScroll: true }), 100);
        }
      }
    }

    this.updateProgress();
    this.updateValueMessage();
    this.saveProgress();

    document.dispatchEvent(new CustomEvent('luvtune:quizStepChange', {
      detail: { step: stepNumber, totalSteps: this.totalSteps, answers: this.answers }
    }));

    if (stepNumber === this.totalSteps) {
      this.populateResults();
    }
  }

  nextStep() {
    if (!this.validateCurrentStep()) return;
    this.collectStepAnswers();
    if (this.currentStep < this.totalSteps) {
      this.currentStep++;
      this.showStep(this.currentStep, true);
    }
  }

  prevStep() {
    if (this.currentStep > 1) {
      this.currentStep--;
      this.showStep(this.currentStep, true);
    }
  }

  // ========================
  // BUNDLE TIER SELECTION
  // ========================

  selectTier(tier) {
    this.selectedTier = tier;
    const config = this.currency || this.getDefaultCurrency();

    // Update bundle card selection
    this.container.querySelectorAll('[data-select-tier]').forEach(card => {
      card.classList.toggle('qb-bundle--selected', card.dataset.selectTier === tier);
    });

    // Switch details panel
    this.container.querySelectorAll('[data-details-for]').forEach(panel => {
      panel.classList.toggle('qb-details__panel--active', panel.dataset.detailsFor === tier);
    });

    // Show/hide second song section
    const secondSong = this.container.querySelector('[data-second-song]');
    if (secondSong) {
      secondSong.style.display = tier === 'ultimate' ? '' : 'none';
    }

    // Update CTA price
    const price = tier === 'ultimate' ? config.ultimate.price : config.essential.price;
    const ctaPrice = this.container.querySelector('[data-cta-price]');
    if (ctaPrice) ctaPrice.textContent = `${config.symbol}${price}`;

    this.saveProgress();
  }

  // ========================
  // PROGRESS & VALUE
  // ========================

  updateProgress() {
    const percent = Math.round((this.currentStep / this.totalSteps) * 100);
    if (this.progressBar) this.progressBar.style.width = percent + '%';
    if (this.progressPercent) {
      this.progressPercent.innerHTML = `Step <span>${this.currentStep}</span> of ${this.totalSteps}`;
    }
  }

  updateValueMessage() {
    const messageEl = this.container.querySelector('[data-value-message]');
    if (messageEl && this.valueMessages[this.currentStep]) {
      messageEl.style.opacity = '0';
      setTimeout(() => {
        messageEl.textContent = this.valueMessages[this.currentStep];
        messageEl.style.opacity = '1';
      }, 200);
    }
  }

  // ========================
  // VALIDATION & DATA
  // ========================

  validateCurrentStep() {
    const currentStepEl = this.container.querySelector(`[data-quiz-step="${this.currentStep}"]`);
    if (!currentStepEl) return true;
    let isValid = true;

    currentStepEl.querySelectorAll('[data-quiz-field][required]').forEach(field => {
      if (!field.value.trim()) {
        this.showFieldError(field, 'This field is required');
        isValid = false;
      } else {
        this.clearFieldError(field);
      }
    });

    currentStepEl.querySelectorAll('[data-option-group][data-required]').forEach(group => {
      if (!group.querySelector('[data-quiz-option].selected')) {
        this.showGroupError(group, 'Please make a selection');
        isValid = false;
      } else {
        this.clearGroupError(group);
      }
    });

    return isValid;
  }

  collectStepAnswers() {
    const stepEl = this.container.querySelector(`[data-quiz-step="${this.currentStep}"]`);
    if (!stepEl) return;

    stepEl.querySelectorAll('[data-quiz-field]').forEach(field => {
      const name = field.dataset.quizField;
      if (name) this.answers[name] = field.value;
    });

    stepEl.querySelectorAll('[data-option-group]').forEach(group => {
      const name = group.dataset.optionGroup;
      const sel = group.querySelector('[data-quiz-option].selected');
      if (name && sel) this.answers[name] = sel.dataset.value;
    });
  }

  collectAllAnswers() {
    this.steps.forEach(stepEl => {
      stepEl.querySelectorAll('[data-quiz-field]').forEach(f => {
        if (f.dataset.quizField && f.value) this.answers[f.dataset.quizField] = f.value;
      });
      stepEl.querySelectorAll('[data-option-group]').forEach(g => {
        const sel = g.querySelector('[data-quiz-option].selected');
        if (g.dataset.optionGroup && sel) this.answers[g.dataset.optionGroup] = sel.dataset.value;
      });
    });
  }

  handleOptionSelect(card) {
    const group = card.closest('[data-option-group]');
    if (!group) return;

    group.querySelectorAll('[data-quiz-option]').forEach(c => {
      c.classList.remove('selected');
      c.setAttribute('aria-checked', 'false');
    });
    card.classList.add('selected');
    card.setAttribute('aria-checked', 'true');
    this.clearGroupError(group);

    if (this.currentStep === 1) setTimeout(() => this.nextStep(), 400);
  }

  handleFieldInput(field) {
    if (field.value.trim()) this.clearFieldError(field);
  }

  populateResults() {
    const results = this.container.querySelector('[data-quiz-results]');
    if (!results) return;

    Object.keys(this.answers).forEach(key => {
      const el = results.querySelector(`[data-result="${key}"]`);
      if (el && this.answers[key]) el.textContent = this.answers[key];
    });
  }

  // ========================
  // SUBMISSION
  // ========================

  handleSubmit() {
    if (!this.validateCurrentStep()) return;
    this.collectAllAnswers();

    const btns = [
      this.container.querySelector('.qb-cta[data-quiz-submit]')
    ].filter(Boolean);

    btns.forEach(btn => {
      btn.disabled = true;
      btn.innerHTML = '<span class="quiz-btn__spinner"></span> Adding to Cart...';
    });

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(this.buildCartData())
    })
    .then(r => { if (!r.ok) throw new Error('Cart error'); return r.json(); })
    .then(data => {
      this.clearProgress();
      document.dispatchEvent(new CustomEvent('luvtune:quizComplete', {
        detail: { answers: this.answers, tier: this.selectedTier, cartData: data }
      }));
      window.location.href = '/checkout';
    })
    .catch(err => {
      console.error('Cart error:', err);
      const config = this.currency || this.getDefaultCurrency();
      const price = this.selectedTier === 'ultimate' ? config.ultimate.price : config.essential.price;

      btns.forEach(btn => {
        btn.disabled = false;
        btn.innerHTML = `<span class="qb-cta__text">Create My Song</span><span class="qb-cta__separator">\u2014</span><span class="qb-cta__price" data-cta-price>${config.symbol}${price}</span>`;
      });
      alert('There was an error adding to cart. Please try again.');
    });
  }

  buildCartData() {
    const variantId = parseInt(this.getResolvedVariantId(), 10);

    if (this.selectedTier === 'ultimate') {
      // Add 2 separate line items — one per song
      const song1Props = {};
      Object.keys(this.answers).forEach(key => {
        if (key.startsWith('Second Song')) return;
        if (this.answers[key]) song1Props[key] = this.answers[key];
      });
      song1Props['Package'] = 'The Complete Gift';
      song1Props['Song'] = 'Song 1 of 2';
      song1Props['Bonus - Printable Lyrics Card'] = 'Yes';

      // Song 2 — copy all from song 1, override genre/mood only if different
      const song2Props = Object.assign({}, song1Props);
      song2Props['Song'] = 'Song 2 of 2';

      const s2Genre = this.answers['Second Song Genre'];
      const s2Mood = this.answers['Second Song Mood'];
      if (s2Genre && s2Genre !== 'Same as first') song2Props['Genre'] = s2Genre;
      if (s2Mood && s2Mood !== 'Same as first') song2Props['Mood'] = s2Mood;

      return {
        items: [
          { id: variantId, quantity: 1, properties: song1Props },
          { id: variantId, quantity: 1, properties: song2Props }
        ]
      };
    }

    // Song Only — single item
    const properties = {};
    Object.keys(this.answers).forEach(key => {
      if (key.startsWith('Second Song')) return;
      if (this.answers[key]) properties[key] = this.answers[key];
    });
    properties['Package'] = 'Song Only';

    return { items: [{ id: variantId, quantity: 1, properties: properties }] };
  }

  getResolvedVariantId() {
    let variantId = this.container.dataset.variantId;
    if (!variantId) {
      const input = this.container.querySelector('[data-product-variant-id]');
      if (input) variantId = input.value;
    }
    if (!variantId) {
      const fallback = document.querySelector('[name="id"]') || document.querySelector('select[name="id"]');
      variantId = fallback ? fallback.value : this.getVariantId();
    }
    return variantId;
  }

  getVariantId() {
    const params = new URLSearchParams(window.location.search);
    if (params.has('variant')) return params.get('variant');
    const json = document.querySelector('[data-product-json]');
    if (json) {
      try { const p = JSON.parse(json.textContent); if (p.variants && p.variants[0]) return p.variants[0].id; } catch(e) {}
    }
    const any = document.querySelector('[name="id"]');
    return any ? any.value : null;
  }

  // ========================
  // GEO PRICING
  // ========================

  setupGeoPricing() {
    // V4 pricing: Essential $29.95, Ultimate $54.95
    // Conversions based on Feb 2026 exchange rates
    const cfg = {
      US:  { symbol: '$',   essential: { price: '29.95' },  ultimate: { price: '54.95',  comparePrice: '110' } },
      CA:  { symbol: 'C$',  essential: { price: '42.95' },  ultimate: { price: '78.95',  comparePrice: '158' } },
      GB:  { symbol: '\u00A3',   essential: { price: '23.95' },  ultimate: { price: '42.95',  comparePrice: '86' } },
      AU:  { symbol: 'A$',  essential: { price: '46.95' },  ultimate: { price: '85.95',  comparePrice: '172' } },
      NZ:  { symbol: 'NZ$', essential: { price: '50.95' },  ultimate: { price: '92.95',  comparePrice: '186' } },
      IE:  { symbol: '\u20AC',   essential: { price: '27.95' },  ultimate: { price: '50.95',  comparePrice: '102' } },
      DEFAULT: { symbol: '$', essential: { price: '29.95' }, ultimate: { price: '54.95', comparePrice: '110' } }
    };

    this.detectCountry().then(cc => {
      const config = cfg[cc] || cfg.DEFAULT;
      this.currency = config;
      this.updatePrices(config);
      window.LuvTunePricing = config;
    });
  }

  getDefaultCurrency() {
    return { symbol: '$', essential: { price: '29.95' }, ultimate: { price: '54.95', comparePrice: '110' } };
  }

  async detectCountry() {
    if (window.Shopify && window.Shopify.country) return window.Shopify.country;
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const map = {
      'America/New_York': 'US', 'America/Los_Angeles': 'US', 'America/Chicago': 'US',
      'America/Denver': 'US', 'America/Phoenix': 'US', 'Pacific/Honolulu': 'US',
      'America/Toronto': 'CA', 'America/Vancouver': 'CA', 'America/Edmonton': 'CA',
      'Europe/London': 'GB', 'Europe/Dublin': 'IE',
      'Australia/Sydney': 'AU', 'Australia/Melbourne': 'AU', 'Australia/Brisbane': 'AU',
      'Australia/Perth': 'AU', 'Australia/Adelaide': 'AU',
      'Pacific/Auckland': 'NZ', 'Pacific/Chatham': 'NZ'
    };
    if (map[tz]) return map[tz];
    try {
      const r = await fetch('https://ipapi.co/json/', { timeout: 3000 });
      const d = await r.json();
      if (d.country_code) return d.country_code;
    } catch(e) {}
    return 'US';
  }

  updatePrices(c) {
    const price = this.selectedTier === 'ultimate' ? c.ultimate.price : c.essential.price;

    // Bundle card prices
    this.container.querySelectorAll('[data-tier-price="essential"]').forEach(el => {
      el.textContent = `${c.symbol}${c.essential.price}`;
    });
    this.container.querySelectorAll('[data-tier-price-alt="essential"]').forEach(el => {
      el.textContent = `${c.symbol}${c.essential.price}`;
    });
    this.container.querySelectorAll('[data-tier-price="ultimate"]').forEach(el => {
      el.textContent = `${c.symbol}${c.ultimate.price}`;
    });

    // Compare price on ultimate bundle card
    const compareEl = this.container.querySelector('[data-tier-compare="ultimate"]');
    if (compareEl) compareEl.textContent = `${c.symbol}${c.ultimate.comparePrice}`;

    // Save badge on ultimate card
    const compareNum = parseFloat(c.ultimate.comparePrice);
    const priceNum = parseFloat(c.ultimate.price);
    const savePercent = Math.round((1 - priceNum / compareNum) * 100);
    const saveEl = this.container.querySelector('[data-tier-save="ultimate"]');
    if (saveEl) saveEl.textContent = `Save ${savePercent}%`;

    // Details panel prices
    const compareDisplay = this.container.querySelector('[data-compare-display]');
    if (compareDisplay) compareDisplay.textContent = `${c.symbol}${c.ultimate.comparePrice}`;
    const priceDisplay = this.container.querySelector('[data-price-display]');
    if (priceDisplay) priceDisplay.textContent = `${c.symbol}${c.ultimate.price}`;
    const essentialPriceDisplay = this.container.querySelector('[data-essential-price-display]');
    if (essentialPriceDisplay) essentialPriceDisplay.textContent = `${c.symbol}${c.essential.price}`;

    // CTA price
    const ctaPrice = this.container.querySelector('[data-cta-price]');
    if (ctaPrice) ctaPrice.textContent = `${c.symbol}${price}`;

    // Scale detail row values for non-USD
    if (c.symbol !== '$') {
      const ratio = parseFloat(c.ultimate.price) / 54.95;
      this.container.querySelectorAll('.qb-details__val').forEach(el => {
        const usdVal = parseInt(el.textContent.replace(/[^0-9]/g, ''), 10);
        if (usdVal) el.textContent = `${c.symbol}${Math.round(usdVal * ratio)}`;
      });
    }
  }

  // ========================
  // PERSISTENCE
  // ========================

  saveProgress() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify({
        currentStep: this.currentStep, answers: this.answers,
        selectedTier: this.selectedTier, timestamp: Date.now()
      }));
    } catch(e) {}
  }

  loadSavedProgress() {
    try {
      const saved = localStorage.getItem(this.storageKey);
      if (!saved) return;
      const data = JSON.parse(saved);
      if (Date.now() - data.timestamp < 86400000) {
        this.currentStep = data.currentStep || 1;
        this.answers = data.answers || {};
        this.selectedTier = data.selectedTier || 'ultimate';
        this.prefillAnswers();
        if (this.selectedTier === 'essential') this.selectTier('essential');
      } else {
        localStorage.removeItem(this.storageKey);
      }
    } catch(e) { localStorage.removeItem(this.storageKey); }
  }

  prefillAnswers() {
    Object.keys(this.answers).forEach(key => {
      const field = this.container.querySelector(`[data-quiz-field="${key}"]`);
      if (field) {
        field.value = this.answers[key];
        if (field.classList.contains('quiz-textarea')) this.updateCharCount(field);
      }
      const opt = this.container.querySelector(`[data-option-group="${key}"] [data-value="${this.answers[key]}"]`);
      if (opt) { opt.classList.add('selected'); opt.setAttribute('aria-checked', 'true'); }
    });
  }

  clearProgress() {
    try { localStorage.removeItem(this.storageKey); } catch(e) {}
    this.answers = {};
  }

  // ========================
  // EXIT INTENT
  // ========================

  setupExitIntent() {
    document.addEventListener('mouseout', (e) => {
      if (e.clientY <= 0 && this.currentStep > 1 && this.currentStep < this.totalSteps && !this.exitModalShown) {
        this.showExitModal();
      }
    });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden' && this.currentStep > 1) this.saveProgress();
    });
    window.addEventListener('beforeunload', () => {
      if (this.currentStep > 1 && this.currentStep < this.totalSteps && Object.keys(this.answers).length > 0) {
        this.saveProgress();
      }
    });
  }

  showExitModal() {
    const modal = document.getElementById('QuizExitModal');
    if (!modal || this.exitModalShown) return;
    this.exitModalShown = true;
    const pct = Math.round((this.currentStep / this.totalSteps) * 100);
    const bar = modal.querySelector('[data-exit-progress-bar]');
    const txt = modal.querySelector('[data-exit-progress]');
    if (bar) bar.style.width = pct + '%';
    if (txt) txt.textContent = pct + '%';

    if (typeof theme !== 'undefined' && theme.Modals) {
      if (!this.exitModalInstance) this.exitModalInstance = new theme.Modals('QuizExitModal', 'quiz-exit');
      this.exitModalInstance.open();
    } else {
      modal.classList.add('modal--active');
      document.body.classList.add('modal-open');
      modal.querySelectorAll('.js-modal-close').forEach(btn => {
        btn.addEventListener('click', () => {
          modal.classList.remove('modal--active');
          document.body.classList.remove('modal-open');
        }, { once: true });
      });
    }
  }

  // ========================
  // UTILITIES
  // ========================

  setupCharCounters() {
    this.container.querySelectorAll('.quiz-textarea').forEach(t => this.updateCharCount(t));
  }

  updateCharCount(textarea) {
    const el = this.container.querySelector(`[data-for="${textarea.id}"]`);
    if (el) el.textContent = textarea.value.length;
  }

  setupDateMin() {
    const d = this.container.querySelector('input[type="date"][data-quiz-field]');
    if (d) { const m = new Date(); m.setDate(m.getDate() + 1); d.min = m.toISOString().split('T')[0]; }
  }

  showFieldError(field, msg) {
    this.clearFieldError(field);
    field.classList.add('quiz-input--error');
    const e = document.createElement('span');
    e.className = 'quiz-field__error';
    e.textContent = msg;
    field.parentNode.appendChild(e);
  }

  clearFieldError(field) {
    field.classList.remove('quiz-input--error');
    const e = field.parentNode.querySelector('.quiz-field__error');
    if (e) e.remove();
  }

  showGroupError(group, msg) {
    this.clearGroupError(group);
    const e = document.createElement('span');
    e.className = 'quiz-group__error';
    e.textContent = msg;
    group.appendChild(e);
  }

  clearGroupError(group) {
    const e = group.querySelector('.quiz-group__error');
    if (e) e.remove();
  }
}

// Init
document.addEventListener('DOMContentLoaded', function() {
  const el = document.getElementById('luvtune-quiz');
  if (el && el.dataset.quizVersion === 'v4') {
    window.luvtuneQuiz = new LuvTuneQuizV4({ containerId: 'luvtune-quiz' });
  }
});

document.addEventListener('shopify:section:load', function(e) {
  const el = e.target.querySelector('#luvtune-quiz');
  if (el && el.dataset.quizVersion === 'v4') {
    window.luvtuneQuiz = new LuvTuneQuizV4({ containerId: 'luvtune-quiz' });
  }
});
